import { useState, useRef, useEffect } from 'react';
import { SVGAssets } from '@business/assets/vectors';
import { t } from 'i18next';
import { Button } from '../Button';

interface DateRangePickerProps {
  placeholder: string;
  onChange: (selected: { months: number[]; years: number[] }) => void;
  value?: { months: number[]; years: number[] }; // ✅ ADDED
}

export const DateRangePicker = ({
  placeholder,
  onChange,
  value // ✅ NEW
}: DateRangePickerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedMonths, setSelectedMonths] = useState<string[]>([]);
  const [selectedYears, setSelectedYears] = useState<string[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const months = [
    { value: '1', label: 'Jan' },
    { value: '2', label: 'Feb' },
    { value: '3', label: 'Mar' },
    { value: '4', label: 'Apr' },
    { value: '5', label: 'May' },
    { value: '6', label: 'Jun' },
    { value: '7', label: 'Jul' },
    { value: '8', label: 'Aug' },
    { value: '9', label: 'Sep' },
    { value: '10', label: 'Oct' },
    { value: '11', label: 'Nov' },
    { value: '12', label: 'Dec' }
  ];

  // ✅ LAST 100 YEARS
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 100 }, (_, i) => {
    const year = currentYear - i;
    return { value: year.toString(), label: year.toString() };
  });

  // ✅ SYNC REDUX/PARENT VALUE → UI
  useEffect(() => {
    if (value) {
      setSelectedMonths(value.months?.map(String) || []);
      setSelectedYears(value.years?.map(String) || []);
    }
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleMonth = (month: string) => {
    const newMonths = selectedMonths.includes(month)
      ? selectedMonths.filter(m => m !== month)
      : [...selectedMonths, month];

    setSelectedMonths(newMonths);
  };

  const toggleYear = (year: string) => {
    const newYears = selectedYears.includes(year)
      ? selectedYears.filter(y => y !== year)
      : [...selectedYears, year];

    setSelectedYears(newYears);
  };

  const handleDone = () => {
    // Require at least one year to be selected
    if (selectedYears.length === 0) {
      return;
    }
    onChange({
      months: selectedMonths.map(Number),
      years: selectedYears.map(Number)
    });
    setIsOpen(false);
  };

  const handleCancel = () => {
    setSelectedMonths([]);
    setSelectedYears([]);
    onChange({
      months: [],
      years: []
    });
    setIsOpen(false);
  };

  const getButtonText = () => {
    const totalSelected = selectedMonths.length + selectedYears.length;
    if (totalSelected === 0) return placeholder;
    return `${placeholder} (${totalSelected})`;
  };

  return (
    <div className="relative mb-2" ref={dropdownRef}>
      <Button
        btnStyle="link"
        onClick={() => setIsOpen(!isOpen)}
        className="pt-2 text-sm text-[#505050] font-semibold rounded-md bg-white hover:bg-gray-50 transition-colors flex items-center gap-2">
        {getButtonText()}
        <SVGAssets.PricingArrow className="w-2 h-2 -rotate-90" />
      </Button>

      {isOpen && (
        <div className="absolute left-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-4 w-80">
          <div className="flex gap-4">
            {/* Months Section */}
            <div className="flex-1">
              <div className="grid grid-cols-3 gap-2">
                {months.map(month => (
                  <button
                    key={month.value}
                    onClick={() => toggleMonth(month.value)}
                    className={`px-3 py-2 text-sm rounded-md transition-colors ${
                      selectedMonths.includes(month.value)
                        ? 'bg-[#49712B] text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}>
                    {month.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Years Section */}
            <div className="w-20 border-l border-gray-200 pl-2">
              <div className="max-h-40 overflow-y-auto pr-2">
                <div className="flex flex-col gap-2">
                  {years.map(year => (
                    <button
                      key={year.value}
                      onClick={() => toggleYear(year.value)}
                      className={`px-3 py-2 text-sm rounded-md transition-colors ${
                        selectedYears.includes(year.value)
                          ? 'bg-[#49712B] text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}>
                      {year.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-2 pt-4 mt-4 border-t border-gray-200">
            <Button
              btnStyle="link"
              onClick={handleCancel}
              className="px-4 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-md">
              {t('cancel')}
            </Button>
            <Button
              btnStyle="no-outline"
              onClick={handleDone}
              disabled={selectedYears.length === 0}
              className={`px-4 py-1.5 text-sm rounded-md transition-colors ${
                selectedYears.length === 0
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed opacity-50'
                  : 'bg-[#49712B] text-white hover:bg-[#3e5a21]'
              }`}>
              {t('done')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
